import { useMutation, useQuery } from '@tanstack/react-query'
import {
  ProductionStagesFilterParams,
  CreateProductionStageParams,
  UpdateProductionStageParams
} from '@/types/production-stage'
import { SortOrderEnum } from '@/enums'
import { useProductionStagesStore } from '@/store'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useProductionStages = (params: ProductionStagesFilterParams) => {
  const fetchProductionStages = useProductionStagesStore(state => state.fetchProductionStages)

  return useQuery({
    queryKey: ['production-stages', params],
    queryFn: () => fetchProductionStages(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useProductionStage = (id: string) => {
  const getProductionStage = useProductionStagesStore(state => state.getProductionStage)

  return useQuery({
    queryKey: ['production-stage', id],
    queryFn: () => getProductionStage(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useCreateProductionStage = () => {
  const createProductionStage = useProductionStagesStore(state => state.createProductionStage)

  return useMutation({
    mutationFn: (params: CreateProductionStageParams) => createProductionStage(params),
    onSuccess: () => {}
  })
}

export const useUpdateProductionStage = () => {
  const updateProductionStage = useProductionStagesStore(state => state.updateProductionStage)

  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: UpdateProductionStageParams }) =>
      updateProductionStage(id, params),
    onSuccess: () => {}
  })
}

export const useDeleteProductionStage = () => {
  const deleteProductionStage = useProductionStagesStore(state => state.deleteProductionStage)

  return useMutation({
    mutationFn: (id: string) => deleteProductionStage(id),
    onSuccess: () => {}
  })
}

export const useBulkDeleteProductionStages = () => {
  const bulkDeleteProductionStages = useProductionStagesStore(state => state.bulkDeleteProductionStages)
  const deselectAllProductionStages = useProductionStagesStore(state => state.deselectAllProductionStages)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteProductionStages(ids),
    onSuccess: response => {
      deselectAllProductionStages()
      handleBulkDeleteSuccess(response)
    }
  })
}

export const useSortProductionStage = () => {
  const sortProductionStage = useProductionStagesStore(state => state.sortProductionStage)

  return useMutation({
    mutationFn: ({ id, type }: { id: string; type: SortOrderEnum }) => sortProductionStage(id, type),
    onSuccess: () => {}
  })
}
